import React, { Suspense, lazy } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { OfflineToast } from './shared/components/OfflineToast';
import LoginPage from './modules/auth/pages/LoginPage';
import AgreementsPage from './modules/agreements/pages/AgreementsPage';
import ProfilePage from './modules/profile/pages/ProfilePage';
import NotificationsInboxPage from './modules/notifications/pages/NotificationsInboxPage';
import NotificationsPage from './modules/notifications/pages/NotificationsPage';
import OidcCallbackPage from './modules/auth/pages/OidcCallbackPage';
import PunchcardHistoryPage from './modules/punchcard/pages/PunchcardHistoryPage';
import PunchcardCalendarPage from './modules/punchcard/pages/PunchcardCalendarPage';
import CollectDetailPage from './modules/punchcard/pages/CollectDetailPage';
import LabsPage from './modules/labs/pages/LabsPage';
import DebugConsolePage from './modules/labs/pages/DebugConsolePage';
import PrefsPage from './modules/settings/pages/PrefsPage';
import PunchcardCardPage from './modules/punchcard/pages/PunchcardCardPage';
import { useAuth } from './auth/AuthProvider';
import { AgreementsProvider } from './context/AgreementsContext';
import { MobileShell } from './shared/components/MobileShell';
import { useAgreementHydration } from './modules/agreements/hooks/useAgreementHydration';

const PleasPage = lazy(() => import(/* webpackChunkName: "pleas" */ './modules/pleas/pages/PleasPage'));
const DataExportPage = lazy(
  () => import(/* webpackChunkName: "data-export" */ './modules/privacy/pages/DataExportPage'),
);
const AccountDeletionPage = lazy(
  () => import(/* webpackChunkName: "account-deletion" */ './modules/privacy/pages/AccountDeletionPage'),
);
const PrivacyPage = lazy(() => import('./modules/privacy/pages/PrivacyPage'));
const TermsPage = lazy(() => import('./modules/privacy/pages/TermsPage'));
const PrivacyPolicyPage = lazy(() => import('./modules/privacy/pages/PrivacyPolicyPage'));
const HomePage = lazy(() => import('./modules/home/pages/HomePage'));

const PageLoader: React.FC = () => (
  <div className="flex min-h-[200px] items-center justify-center">
    <Loader2 className="h-8 w-8 animate-spin text-slate-400" aria-label="Carregando" />
  </div>
);

// Paths rendered without MobileShell (full-screen layouts that manage their own chrome)
const FULL_SCREEN_PATHS = new Set(['/login', '/login/callback', '/debug-console']);

function AgreementHydrator(): null {
  useAgreementHydration();
  return null;
}

function AuthenticatedRoutes() {
  const location = useLocation();
  const isFullScreen = FULL_SCREEN_PATHS.has(location.pathname);

  const routes = (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/login/callback" element={<OidcCallbackPage />} />
      <Route
        path="/home"
        element={
          <Suspense fallback={<PageLoader />}>
            <HomePage />
          </Suspense>
        }
      />
      <Route path="/agreements" element={<AgreementsPage />} />
      <Route path="/punchcard" element={<PunchcardCardPage />} />
      <Route path="/punchcard/history" element={<PunchcardHistoryPage />} />
      <Route path="/punchcard/calendar" element={<PunchcardCalendarPage />} />
      <Route path="/punchcard/collect/:nsr" element={<CollectDetailPage />} />
      <Route
        path="/pleas"
        element={
          <Suspense fallback={<PageLoader />}>
            <PleasPage />
          </Suspense>
        }
      />
      <Route path="/profile" element={<ProfilePage />} />
      <Route
        path="/privacy"
        element={
          <Suspense fallback={<PageLoader />}>
            <PrivacyPage />
          </Suspense>
        }
      />
      <Route
        path="/terms"
        element={
          <Suspense fallback={<PageLoader />}>
            <TermsPage />
          </Suspense>
        }
      />
      <Route
        path="/privacy-policy"
        element={
          <Suspense fallback={<PageLoader />}>
            <PrivacyPolicyPage />
          </Suspense>
        }
      />
      <Route path="/notifications" element={<NotificationsInboxPage />} />
      <Route path="/notifications/settings" element={<NotificationsPage />} />
      <Route
        path="/data-export"
        element={
          <Suspense fallback={<PageLoader />}>
            <DataExportPage />
          </Suspense>
        }
      />
      <Route
        path="/account-deletion"
        element={
          <Suspense fallback={<PageLoader />}>
            <AccountDeletionPage />
          </Suspense>
        }
      />
      <Route path="/labs" element={<LabsPage />} />
      <Route path="/prefs" element={<PrefsPage />} />
      <Route path="/debug-console" element={<DebugConsolePage />} />
      <Route path="*" element={<Navigate to="/home" />} />
    </Routes>
  );

  return (
    <AgreementsProvider>
      <AgreementHydrator />
      {isFullScreen ? routes : <MobileShell>{routes}</MobileShell>}
    </AgreementsProvider>
  );
}

const App: React.FC = () => {
  const auth = useAuth();

  return (
    <Router>
      <OfflineToast />
      {auth.isLoading ? null : auth.isAuthenticated ? (
        <AuthenticatedRoutes />
      ) : (
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/login/callback" element={<OidcCallbackPage />} />
          <Route
            path="/terms"
            element={
              <Suspense fallback={<PageLoader />}>
                <TermsPage />
              </Suspense>
            }
          />
          <Route
            path="/privacy-policy"
            element={
              <Suspense fallback={<PageLoader />}>
                <PrivacyPolicyPage />
              </Suspense>
            }
          />
          <Route path="*" element={<Navigate to="/login" />} />
        </Routes>
      )}
    </Router>
  );
};

export default App;
